import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Download, Gauge, Plus } from "lucide-react";

import { ApiError, formatDateTime } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  Input,
  Select,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  EQUIP_LABELS,
  TIPO_PCP_LABELS,
  UNIDAD_LABELS,
  crearMedicion,
  crearPCP,
  desactivarPCP,
  descargarCsvMediciones,
  fetchMediciones,
  fetchPCPs,
  type EquipamientoPresion,
  type MedicionCreateInput,
  type PCPCreateInput,
  type TipoPCP,
  type UnidadPresion,
} from "./api";

export function PresionesPanel(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [crearPcpAbierto, setCrearPcpAbierto] = useState(false);
  const [crearMedAbierto, setCrearMedAbierto] = useState(false);
  const [puntoFiltro, setPuntoFiltro] = useState<number | "">("");
  const [soloFuera, setSoloFuera] = useState(false);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [pcpADesactivar, setPcpADesactivar] = useState<{
    id: number;
    codigo: string;
  } | null>(null);

  const pcpsQ = useQuery({
    queryKey: ["pcps"],
    queryFn: () => fetchPCPs(false),
  });

  const filtros = {
    punto_id: puntoFiltro || null,
    solo_fuera_de_rango: soloFuera,
    desde: desde || null,
    hasta: hasta || null,
    limit: 100,
  };
  const medicionesQ = useQuery({
    queryKey: ["mediciones", filtros],
    queryFn: () => fetchMediciones(filtros),
  });

  const exportMut = useMutation({
    mutationFn: () => descargarCsvMediciones(filtros),
    onSuccess: () => toast.success("CSV descargado"),
    onError: () => toast.error("No se pudo exportar"),
  });

  const desactivarMut = useMutation({
    mutationFn: (id: number) => desactivarPCP(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pcps"] });
      toast.success("PCP desactivado");
      setPcpADesactivar(null);
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "Error";
      toast.error(msg);
    },
  });

  const pcps = pcpsQ.data ?? [];
  const mediciones = medicionesQ.data?.items ?? [];
  const pcpsActivos = pcps.filter((p) => p.activo);

  function pcpById(id: number): string {
    const p = pcps.find((x) => x.id === id);
    return p ? `${p.codigo} (${p.tipo})` : `#${id}`;
  }

  return (
    <div className="space-y-6 pt-4">
      {/* PCPs */}
      <section>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-neutral-800">
              Puntos de Control de Presión (PCPs)
            </h2>
            <p className="text-xs text-neutral-500">
              El monitoreo de presiones se activa cuando la SISS lo instruye
              tras reclamo (Manual Presiones, cap. 5).
            </p>
          </div>
          <Button icon={Plus} onClick={() => setCrearPcpAbierto(true)}>
            Nuevo PCP
          </Button>
        </div>

        {pcpsQ.isPending ? (
          <Cargando />
        ) : pcpsQ.isError ? (
          <ErrorCarga error={pcpsQ.error} onReintentar={() => pcpsQ.refetch()} />
        ) : pcps.length === 0 ? (
          <p className="mt-3 text-sm text-neutral-500">
            Aún no hay PCPs configurados.
          </p>
        ) : (
          <TableWrap className="mt-3" label="Puntos de control de presión">
            <table className="min-w-full divide-y divide-neutral-200 text-sm">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left">Código</th>
                  <th scope="col" className="px-3 py-2 text-left">Tipo</th>
                  <th scope="col" className="px-3 py-2 text-left">Ubicación</th>
                  <th scope="col" className="px-3 py-2 text-left">Estado</th>
                  <th scope="col" className="w-20"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {pcps.map((p) => (
                  <tr key={p.id}>
                    <td className="px-3 py-2 font-mono text-xs">{p.codigo}</td>
                    <td className="px-3 py-2">{TIPO_PCP_LABELS[p.tipo]}</td>
                    <td className="px-3 py-2 text-neutral-600">{p.ubicacion}</td>
                    <td className="px-3 py-2">
                      {p.activo ? (
                        <Badge tone="emerald">Activo</Badge>
                      ) : (
                        <Badge tone="slate">Inactivo</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {p.activo && (
                        <Button
                          variant="linkDanger"
                          size="sm"
                          onClick={() =>
                            setPcpADesactivar({ id: p.id, codigo: p.codigo })
                          }
                        >
                          Desactivar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </section>

      {/* Mediciones */}
      <section>
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-neutral-800">Mediciones</h2>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              icon={Download}
              onClick={() => exportMut.mutate()}
              disabled={exportMut.isPending}
            >
              CSV (SISS)
            </Button>
            <Button
              icon={Plus}
              onClick={() => {
                if (pcpsActivos.length === 0) {
                  toast.error("Primero crea al menos un PCP activo");
                  return;
                }
                setCrearMedAbierto(true);
              }}
            >
              Nueva medición
            </Button>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="PCP">
            <select
              value={puntoFiltro}
              onChange={(e) =>
                setPuntoFiltro(e.target.value ? Number(e.target.value) : "")
              }
              className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm"
            >
              <option value="">Todos</option>
              {pcps.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.codigo} — {TIPO_PCP_LABELS[p.tipo]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Desde">
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm"
            />
          </Field>
          <Field label="Hasta">
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm"
            />
          </Field>
          <div className="flex items-end pb-1">
            <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={soloFuera}
                onChange={(e) => setSoloFuera(e.target.checked)}
              />
              Sólo fuera de rango
            </label>
          </div>
        </div>

        {medicionesQ.isPending ? (
          <Cargando />
        ) : medicionesQ.isError ? (
          <ErrorCarga error={medicionesQ.error} onReintentar={() => medicionesQ.refetch()} />
        ) : mediciones.length === 0 ? (
          <p className="mt-3 text-sm text-neutral-500">
            Sin mediciones para los filtros aplicados.
          </p>
        ) : (
          <TableWrap className="mt-3" label="Mediciones de presión">
            <table className="min-w-full divide-y divide-neutral-200 text-sm">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left">Fecha/Hora</th>
                  <th scope="col" className="px-3 py-2 text-left">PCP</th>
                  <th scope="col" className="px-3 py-2 text-left">Responsable</th>
                  <th scope="col" className="px-3 py-2 text-right">Valor</th>
                  <th scope="col" className="px-3 py-2 text-left">Equipamiento</th>
                  <th scope="col" className="px-3 py-2 text-left">Rango</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {mediciones.map((m) => (
                  <tr key={m.id} className="hover:bg-neutral-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatDateTime(m.fecha_medicion)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {pcpById(m.punto_id)}
                    </td>
                    <td className="px-3 py-2 text-neutral-700">
                      {m.responsable}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {m.valor} {UNIDAD_LABELS[m.unidad]}
                    </td>
                    <td className="px-3 py-2 text-xs text-neutral-600">
                      {EQUIP_LABELS[m.equipamiento]}
                    </td>
                    <td className="px-3 py-2">
                      {m.fuera_de_rango ? (
                        <Badge tone="rose">
                          <AlertTriangle className="h-3 w-3" strokeWidth={1.5} />
                          Fuera
                        </Badge>
                      ) : (
                        <Badge tone="emerald">OK</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </section>

      {crearPcpAbierto && (
        <CrearPCPModal
          onClose={() => setCrearPcpAbierto(false)}
          onCreated={() => {
            setCrearPcpAbierto(false);
            qc.invalidateQueries({ queryKey: ["pcps"] });
            toast.success("PCP creado");
          }}
        />
      )}
      {crearMedAbierto && (
        <CrearMedicionModal
          pcps={pcpsActivos}
          onClose={() => setCrearMedAbierto(false)}
          onCreated={() => {
            setCrearMedAbierto(false);
            qc.invalidateQueries({ queryKey: ["mediciones"] });
            toast.success("Medición registrada");
          }}
        />
      )}

      <ConfirmDialog
        open={pcpADesactivar !== null}
        title="Desactivar PCP"
        description={
          pcpADesactivar
            ? `Se desactivará el punto ${pcpADesactivar.codigo}. Sus mediciones históricas se conservan.`
            : undefined
        }
        confirmLabel="Desactivar"
        loading={desactivarMut.isPending}
        onConfirm={() => {
          if (pcpADesactivar) desactivarMut.mutate(pcpADesactivar.id);
        }}
        onCancel={() => setPcpADesactivar(null)}
      />
    </div>
  );
}

function CrearPCPModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const toast = useToast();
  const [codigo, setCodigo] = useState("");
  const [ubicacion, setUbicacion] = useState("");
  const [tipo, setTipo] = useState<TipoPCP>("Minima");
  const [observaciones, setObservaciones] = useState("");

  const mut = useMutation({
    mutationFn: (input: PCPCreateInput) => crearPCP(input),
    onSuccess: () => onCreated(),
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "Error al crear PCP";
      toast.error(msg);
    },
  });

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!codigo.trim() || !ubicacion.trim()) {
      toast.error("Código y ubicación son obligatorios");
      return;
    }
    mut.mutate({
      codigo: codigo.trim(),
      ubicacion: ubicacion.trim(),
      tipo,
      observaciones: observaciones.trim() || null,
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([codigo, ubicacion, tipo, observaciones]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nuevo PCP"
      icon={Gauge}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form="crear-pcp-form" disabled={mut.isPending}>
            {mut.isPending ? "Guardando…" : "Crear"}
          </Button>
        </>
      }
    >
      <form id="crear-pcp-form" onSubmit={onSubmit} className="space-y-3">
        <Input
          label="Código"
          required
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          placeholder="PCP-1"
        />
        <Select
          label="Tipo"
          required
          value={tipo}
          onChange={(e) => setTipo(e.target.value as TipoPCP)}
        >
          <option value="Minima">{TIPO_PCP_LABELS.Minima}</option>
          <option value="Maxima">{TIPO_PCP_LABELS.Maxima}</option>
        </Select>
        <Input
          label="Ubicación"
          required
          value={ubicacion}
          onChange={(e) => setUbicacion(e.target.value)}
          placeholder="Calle / cerro / sector"
        />
        <Textarea
          label="Observaciones"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows={2}
        />
      </form>
      {dialogoDescarte}
    </Modal>
  );
}

function CrearMedicionModal({
  pcps,
  onClose,
  onCreated,
}: {
  pcps: { id: number; codigo: string; tipo: TipoPCP }[];
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const toast = useToast();
  const [puntoId, setPuntoId] = useState<number>(pcps[0]?.id ?? 0);
  const [fecha, setFecha] = useState("");
  const [responsable, setResponsable] = useState("");
  const [valor, setValor] = useState("");
  const [unidad, setUnidad] = useState<UnidadPresion>("mca");
  const [equipamiento, setEquipamiento] =
    useState<EquipamientoPresion>("Manometro");
  const [observaciones, setObservaciones] = useState("");

  const mut = useMutation({
    mutationFn: (input: MedicionCreateInput) => crearMedicion(input),
    onSuccess: () => onCreated(),
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "Error";
      toast.error(msg);
    },
  });

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!puntoId || !fecha || !responsable.trim() || !valor) {
      toast.error("Faltan datos obligatorios");
      return;
    }
    mut.mutate({
      punto_id: puntoId,
      fecha_medicion: new Date(fecha).toISOString(),
      responsable: responsable.trim(),
      valor,
      unidad,
      equipamiento,
      observaciones: observaciones.trim() || null,
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([puntoId, fecha, responsable, valor, unidad, observaciones]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nueva medición de presión"
      icon={Gauge}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form="crear-medicion-form" disabled={mut.isPending}>
            {mut.isPending ? "Guardando…" : "Registrar"}
          </Button>
        </>
      }
    >
      <form id="crear-medicion-form" onSubmit={onSubmit} className="space-y-3">
        <Select
          label="PCP"
          required
          value={puntoId}
          onChange={(e) => setPuntoId(Number(e.target.value))}
        >
          {pcps.map((p) => (
            <option key={p.id} value={p.id}>
              {p.codigo} — {TIPO_PCP_LABELS[p.tipo]}
            </option>
          ))}
        </Select>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Fecha y hora"
            required
            type="datetime-local"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
          <Input
            label="Responsable"
            required
            value={responsable}
            onChange={(e) => setResponsable(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Valor"
            required
            type="number"
            step="0.001"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
          />
          <Select
            label="Unidad"
            value={unidad}
            onChange={(e) => setUnidad(e.target.value as UnidadPresion)}
          >
            <option value="mca">m.c.a.</option>
            <option value="bar">BAR</option>
            <option value="psi">PSI</option>
          </Select>
          <Select
            label="Equipamiento"
            value={equipamiento}
            onChange={(e) =>
              setEquipamiento(e.target.value as EquipamientoPresion)
            }
          >
            <option value="Manometro">Manómetro</option>
            <option value="ManometroAgujaArrastre">Aguja de arrastre</option>
            <option value="Datalogger">Datalogger</option>
          </Select>
        </div>
        <Textarea
          label="Observaciones"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows={2}
        />
      </form>
      {dialogoDescarte}
    </Modal>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-neutral-600">
        {label}
      </label>
      {children}
    </div>
  );
}
